/**
 * Secure storage utilities for handling sensitive data
 * Provides encrypted storage and secure cleanup mechanisms
 */

// Simple obfuscation (not real encryption, but better than plain text)
// For production, consider using a proper encryption library
const obfuscate = (data: string): string => {
  return btoa(encodeURIComponent(data));
};

const deobfuscate = (data: string): string => {
  try {
    return decodeURIComponent(atob(data));
  } catch {
    return '';
  }
};

// Secure storage interface
export class SecureStorage {
  private static readonly SENSITIVE_KEYS = [
    'userIsAdmin',
    'hg-quiz-auth',
    'user_session',
    'auth_token'
  ];

  // Store sensitive data with obfuscation
  static setSecureItem(key: string, value: string, useSessionStorage = false): void {
    try {
      const storage = useSessionStorage ? sessionStorage : localStorage;
      const obfuscatedValue = this.SENSITIVE_KEYS.includes(key) ? obfuscate(value) : value;
      storage.setItem(key, obfuscatedValue);
    } catch (error) {
      console.error(`Error storing ${key}:`, error);
    }
  }

  // Retrieve sensitive data with deobfuscation
  static getSecureItem(key: string, useSessionStorage = false): string | null {
    try {
      const storage = useSessionStorage ? sessionStorage : localStorage;
      const storedValue = storage.getItem(key);
      
      if (!storedValue) return null;
      
      return this.SENSITIVE_KEYS.includes(key) ? deobfuscate(storedValue) : storedValue;
    } catch (error) {
      console.error(`Error retrieving ${key}:`, error);
      return null;
    }
  }

  // Remove item securely
  static removeSecureItem(key: string, useSessionStorage = false): void {
    try {
      const storage = useSessionStorage ? sessionStorage : localStorage;
      storage.removeItem(key);
    } catch (error) {
      console.error(`Error removing ${key}:`, error);
    }
  }

  // Clear all sensitive data
  static clearSensitiveData(): void {
    try {
      // Clear specific sensitive keys from localStorage
      this.SENSITIVE_KEYS.forEach(key => {
        localStorage.removeItem(key);
      });
      
      // Clear all sessionStorage (it's session-specific anyway)
      sessionStorage.clear();
      
      console.log('Sensitive data cleared successfully');
    } catch (error) {
      console.error('Error clearing sensitive data:', error);
    }
  }

  // Store boolean values securely
  static setSecureBooleanItem(key: string, value: boolean, useSessionStorage = false): void {
    this.setSecureItem(key, value.toString(), useSessionStorage);
  }

  // Retrieve boolean values securely
  static getSecureBooleanItem(key: string, useSessionStorage = false): boolean | null {
    const value = this.getSecureItem(key, useSessionStorage);
    if (value === null) return null;
    return value === 'true';
  }

  // Validate storage quota and clean up if needed
  static validateStorageQuota(): boolean {
    try {
      const testKey = '__storage_test__';
      const testValue = 'test';
      localStorage.setItem(testKey, testValue);
      localStorage.removeItem(testKey);
      return true;
    } catch {
      // Storage quota exceeded or unavailable
      console.warn('Storage quota exceeded or unavailable');
      return false;
    }
  }
}

// Non-sensitive storage for regular app data
export class AppStorage {
  // Store non-sensitive application data
  static setItem(key: string, value: string, useSessionStorage = false): void {
    try {
      const storage = useSessionStorage ? sessionStorage : localStorage;
      storage.setItem(key, value);
    } catch (error) {
      console.error(`Error storing ${key}:`, error);
    }
  }

  // Retrieve non-sensitive application data
  static getItem(key: string, useSessionStorage = false): string | null {
    try {
      const storage = useSessionStorage ? sessionStorage : localStorage;
      return storage.getItem(key);
    } catch (error) {
      console.error(`Error retrieving ${key}:`, error);
      return null;
    }
  }

  // Store boolean values
  static setBooleanItem(key: string, value: boolean, useSessionStorage = false): void {
    this.setItem(key, value.toString(), useSessionStorage);
  }

  // Retrieve boolean values
  static getBooleanItem(key: string, useSessionStorage = false): boolean | null {
    const value = this.getItem(key, useSessionStorage);
    if (value === null) return null;
    return value === 'true';
  }
}